import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import './XbrlConverter.css';
import api from '../services/api';

const FINAL_STATUSES = new Set(['COMPLETED', 'FAILED']);
const POLL_INTERVAL_MS = 5000;

function XbrlConverter({ user }) {
  const [file, setFile] = useState(null);
  const [ticker, setTicker] = useState('');
  const [company, setCompany] = useState('');
  const [cik, setCik] = useState('');
  const [emailOnComplete, setEmailOnComplete] = useState(true);
  const [emailTo, setEmailTo] = useState(user?.email || '');
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [emailModal, setEmailModal] = useState(null); // { jobId, email, sending, error, success }
  const pollerRef = useRef(null);

  // Load existing jobs on mount
  useEffect(() => {
    let canceled = false;
    const loadJobs = async () => {
      try {
        const res = await api.get('converter/jobs/');
        if (canceled) return;
        const jobList = res.data?.results ?? res.data ?? [];
        setJobs(Array.isArray(jobList) ? jobList : []);
        jobList.forEach(job => {
          if (!FINAL_STATUSES.has(job.status)) {
            setActiveJob(job);
          }
        });
      } catch (err) {
        if (!canceled) console.error('Failed to load jobs', err);
      }
    };
    loadJobs();
    return () => { canceled = true; };
  }, []);

  // Poll active job
  useEffect(() => {
    if (!activeJob || FINAL_STATUSES.has(activeJob.status)) {
      if (pollerRef.current) {
        clearInterval(pollerRef.current);
        pollerRef.current = null;
      }
      return;
    }

    const pollJob = async () => {
      try {
        const res = await api.get(`converter/jobs/${activeJob.id}/`);
        const updated = res.data;
        setActiveJob(updated);
        setJobs(prev => prev.map(j => j.id === updated.id ? updated : j));
        if (FINAL_STATUSES.has(updated.status)) {
          clearInterval(pollerRef.current);
          pollerRef.current = null;
          const listRes = await api.get('converter/jobs/');
          const jobList = listRes.data?.results ?? listRes.data ?? [];
          setJobs(Array.isArray(jobList) ? jobList : []);
        }
      } catch (err) {
        console.error('Poll error', err);
      }
    };

    pollJob();
    pollerRef.current = setInterval(pollJob, POLL_INTERVAL_MS);
    return () => {
      if (pollerRef.current) {
        clearInterval(pollerRef.current);
        pollerRef.current = null;
      }
    };
  }, [activeJob?.id, activeJob?.status]);

  const onDrop = useCallback((accepted) => {
    if (accepted.length > 0) {
      setFile(accepted[0]);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/html': ['.htm', '.html'] },
  });

  const removeFile = () => setFile(null);

  const handleUpload = async () => {
    if (!file) { setError('Please select an .htm file first.'); return; }
    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Ensure CSRF cookie is set before upload
      await api.get('auth/csrf/');
      await new Promise(r => setTimeout(r, 100));

      const formData = new FormData();
      formData.append('file', file);
      if (ticker.trim()) formData.append('ticker', ticker.trim());
      if (company.trim()) formData.append('company', company.trim());
      if (cik.trim()) formData.append('cik', cik.trim());
      if (emailOnComplete && user?.email) formData.append('email_on_complete', user.email);

      const res = await api.post('converter/upload/', formData, {
        onUploadProgress: evt => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });

      const job = res.data?.job || res.data;
      const jobWithStatus = { ...job, status: job.status || 'PROCESSING' };
      setActiveJob(jobWithStatus);
      setJobs(prev => [jobWithStatus, ...prev.filter(j => j.id !== jobWithStatus.id)]);
      setFile(null);
      setTicker('');
      setCompany('');
      setCik('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const downloadZip = async (job) => {
    let url = job.download_url || `/api/converter/jobs/${job.id}/download/`;
    if (url.startsWith('/api/')) url = url.slice(5);
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const origName = job.meta?.original_name || `job_${job.id}`;
      a.download = origName.replace(/\.(htm|html)$/i, '') + '_xbrl.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      alert('Download failed');
    }
  };

  const openEmailModal = (job) => {
    setEmailModal({
      jobId: job.id,
      email: user?.email || '',
      sending: false,
      error: null,
      success: false,
    });
  };

  const sendEmail = async () => {
    if (!emailModal) return;
    setEmailModal(prev => ({ ...prev, sending: true, error: null }));

    try {
      await api.post(`converter/jobs/${emailModal.jobId}/email/`, {
        email: emailModal.email,
      });
      setEmailModal(prev => ({ ...prev, sending: false, success: true }));
      setTimeout(() => setEmailModal(null), 2000);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to send email';
      setEmailModal(prev => ({ ...prev, sending: false, error: msg }));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getFileName = (job) => {
    return job.meta?.original_name || job.input_file?.split('/').pop() || `Job #${job.id}`;
  };

  const isProcessing = activeJob && !FINAL_STATUSES.has(activeJob.status);

  return (
    <div className="xbrl-container">
      <h2>Convert HTML Filing to XBRL</h2>

      {/* Dropzone */}
      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
        <input {...getInputProps()} />
        <div className="dropzone-content">
          <svg className="dropzone-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 32V16M24 16L18 22M24 16L30 22" stroke="#4a5568" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 32V38C8 39.1046 8.89543 40 10 40H38C39.1046 40 40 39.1046 40 38V32" stroke="#4a5568" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {file ? (
            <p className="dropzone-selected">
              Selected: <strong>{file.name}</strong>
              <button type="button" className="remove-file-inline" onClick={(e) => { e.stopPropagation(); removeFile(); }}>Remove</button>
            </p>
          ) : (
            <p>Drag & drop an .htm file here, or click to browse</p>
          )}
        </div>
      </div>

      {/* Optional fields */}
      <div className="options-panel">
        <h3>Optional Settings</h3>
        <p className="options-hint">Leave blank to auto-detect from the filing</p>
        <div className="options-grid">
          <div className="option-field">
            <label htmlFor="ticker">Ticker</label>
            <input id="ticker" type="text" value={ticker} onChange={e => setTicker(e.target.value)} placeholder="e.g., AAPL" disabled={uploading} />
          </div>
          <div className="option-field">
            <label htmlFor="company">Company Name</label>
            <input id="company" type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="e.g., Apple Inc." disabled={uploading} />
          </div>
          <div className="option-field">
            <label htmlFor="cik">CIK Number</label>
            <input id="cik" type="text" value={cik} onChange={e => setCik(e.target.value)} placeholder="e.g., 0000320193" disabled={uploading} />
          </div>
        </div>
      </div>

      {/* Email on completion */}
      <div className="email-option-panel">
        <label className="email-toggle">
          <input
            type="checkbox"
            checked={emailOnComplete}
            onChange={e => setEmailOnComplete(e.target.checked)}
            disabled={uploading}
          />
          <span>Email results when complete</span>
        </label>
        {emailOnComplete && (
          <p className="email-to-display">Email will be sent to: <strong>{user?.email}</strong></p>
        )}
      </div>

      {/* Upload button */}
      <button type="button" className="upload-btn action-btn primary" onClick={handleUpload} disabled={!file || uploading}>
        {uploading ? (<><span className="spinner" aria-hidden="true" /> Uploading... {uploadProgress}%</>) : 'Upload & Convert'}
      </button>

      {error && <p className="error-message">{error}</p>}

      {/* Processing indicator */}
      {isProcessing && (
        <div className="processing-panel">
          <span className="spinner" aria-hidden="true" />
          <span>Converting <strong>{getFileName(activeJob)}</strong>... This may take a few minutes.</span>
        </div>
      )}

      {/* Conversions table */}
      {jobs.length > 0 && (
        <div className="job-history">
          <h3>Conversions</h3>
          <table className="conversions-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Status</th>
                <th>Completed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job.id} className={activeJob?.id === job.id ? 'active-row' : ''}>
                  <td className="cell-filename">{getFileName(job)}</td>
                  <td>
                    <span className={`status-badge ${(job.status || '').toLowerCase()}`}>
                      {job.status === 'COMPLETED' ? 'Completed' :
                       job.status === 'FAILED' ? 'Failed' :
                       job.status === 'PROCESSING' ? 'Processing...' : 'Queued'}
                    </span>
                  </td>
                  <td className="cell-date">
                    {job.status === 'COMPLETED' ? formatDate(job.updated_at) :
                     job.status === 'FAILED' ? 'Failed' :
                     job.status === 'PROCESSING' ? <span className="spinner-small" /> : '\u2014'}
                  </td>
                  <td className="cell-actions">
                    {job.status === 'COMPLETED' && job.download_url ? (
                      <>
                        <button type="button" className="download-btn" onClick={() => downloadZip(job)} title="Download ZIP">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                          ZIP
                        </button>
                        <button type="button" className="email-btn" onClick={() => openEmailModal(job)} title="Email result">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: 'text-bottom' }}>
                            <rect x="2" y="4" width="20" height="16" rx="2"/>
                            <path d="M22 7l-10 7L2 7"/>
                          </svg>
                          Email
                        </button>
                      </>
                    ) : '\u2014'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Failed job error */}
      {activeJob && activeJob.status === 'FAILED' && (
        <div className="failed-panel">
          <h3>Conversion Failed</h3>
          <p className="fail-message">{activeJob.meta?.error || 'An unexpected error occurred.'}</p>
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <div className="modal-overlay" onClick={() => !emailModal.sending && setEmailModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Email XBRL Output</h3>
            {emailModal.success ? (
              <div className="email-success">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>Sent to {emailModal.email}</span>
              </div>
            ) : (
              <>
                <div className="email-field">
                  <label htmlFor="email-to">Send to</label>
                  <input
                    id="email-to"
                    type="email"
                    value={emailModal.email}
                    onChange={e => setEmailModal(prev => ({ ...prev, email: e.target.value, error: null }))}
                    placeholder="recipient@example.com"
                    disabled={emailModal.sending}
                  />
                </div>
                {emailModal.error && <p className="email-error">{emailModal.error}</p>}
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={() => setEmailModal(null)} disabled={emailModal.sending}>
                    Cancel
                  </button>
                  <button type="button" className="send-btn" onClick={sendEmail} disabled={emailModal.sending || !emailModal.email}>
                    {emailModal.sending ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default XbrlConverter;
